import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEstablishment } from '../components/EstablishmentSelector';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Plus, Search, Edit, Trash2, UtensilsCrossed, Minus, ChefHat } from 'lucide-react';

// v5.0 - Recipe = plat vendu avec prix de vente et ingrédients
const RecipesPage = () => {
    const { api, hasPermission } = useAuth();
    const { selectedEstablishmentId } = useEstablishment();
    const [recipes, setRecipes] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRecipe, setEditingRecipe] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [detailRecipe, setDetailRecipe] = useState(null);
    
    // Permissions
    const canViewCosts = hasPermission?.('view_recipe_costs') ?? false;
    const canCreate = hasPermission?.('create_recipe') ?? false;
    const canEdit = hasPermission?.('edit_recipe') ?? false;
    const canDelete = hasPermission?.('delete_recipe') ?? false;

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        sale_price_cfa: '',
        is_active: true,
        ingredients: []
    });

    useEffect(() => {
        fetchData();
    }, [selectedEstablishmentId]);

    const fetchData = async () => {
        try {
            // Build query with establishment filter
            const recipeParams = selectedEstablishmentId 
                ? `/recipes?establishment_id=${selectedEstablishmentId}` 
                : '/recipes';
            const productParams = selectedEstablishmentId 
                ? `/products?establishment_id=${selectedEstablishmentId}` 
                : '/products';
            
            const [recipesRes, productsRes] = await Promise.all([
                api().get(recipeParams),
                api().get(productParams)
            ]);
            setRecipes(recipesRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (formData.ingredients.length === 0) {
                toast.error('Ajoutez au moins un ingrédient');
                return;
            }

            const payload = {
                name: formData.name,
                sale_price_cfa: parseFloat(formData.sale_price_cfa) || 0,
                is_active: formData.is_active,
                ingredients: formData.ingredients.map(ing => ({
                    product_id: ing.product_id,
                    quantity_base: parseFloat(ing.quantity_base) || 0
                }))
            };

            if (editingRecipe) {
                await api().put(`/recipes/${editingRecipe.id}`, payload);
                toast.success('Recette mise à jour');
            } else {
                await api().post('/recipes', payload);
                toast.success('Recette créée');
            }

            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    const handleEdit = (recipe) => {
        setEditingRecipe(recipe);
        setFormData({
            name: recipe.name,
            sale_price_cfa: recipe.sale_price_cfa?.toString() || '',
            is_active: recipe.is_active !== false,
            ingredients: recipe.ingredients.map(ing => ({
                product_id: ing.product_id,
                quantity_base: ing.quantity_base?.toString() || '0',
                product_name: ing.product_name || products.find(p => p.id === ing.product_id)?.name
            }))
        });
        setDialogOpen(true);
    };

    const handleDelete = async (recipeId) => {
        if (!window.confirm('Supprimer cette recette ?')) return;
        try {
            await api().delete(`/recipes/${recipeId}`);
            toast.success('Recette supprimée');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const resetForm = () => {
        setEditingRecipe(null);
        setFormData({
            name: '',
            sale_price_cfa: '',
            is_active: true,
            ingredients: []
        });
    };

    const addIngredient = () => {
        setFormData({
            ...formData,
            ingredients: [
                ...formData.ingredients,
                { product_id: '', quantity_base: '', product_name: '' }
            ]
        });
    };

    const removeIngredient = (index) => {
        const newIngredients = [...formData.ingredients];
        newIngredients.splice(index, 1);
        setFormData({ ...formData, ingredients: newIngredients });
    };

    const updateIngredient = (index, field, value) => {
        const newIngredients = [...formData.ingredients];
        newIngredients[index][field] = value;
        
        if (field === 'product_id') {
            const product = products.find(p => p.id === value);
            newIngredients[index].product_name = product?.name || '';
        }
        
        setFormData({ ...formData, ingredients: newIngredients });
    };

    const filteredRecipes = recipes.filter(r => 
        r.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="recipes-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Recettes</h1>
                    <p className="text-gray-500">Plats vendus ({recipes.length})</p>
                </div>
                {canCreate && (
                    <Button 
                        onClick={() => { resetForm(); setDialogOpen(true); }}
                        data-testid="add-recipe-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle Recette
                    </Button>
                )}
            </div>

            {/* Recherche */}
            <Card>
                <CardContent className="pt-6">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Rechercher une recette..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Liste des recettes */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ChefHat className="w-5 h-5" />
                        Liste des recettes
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Recette</TableHead>
                                {canViewCosts && <TableHead>Prix vente</TableHead>}
                                {canViewCosts && <TableHead>Coût matière</TableHead>}
                                {canViewCosts && <TableHead>Marge</TableHead>}
                                <TableHead>Ingrédients</TableHead>
                                <TableHead>Statut</TableHead>
                                {canEdit && <TableHead className="text-right">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredRecipes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                                        Aucune recette trouvée
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRecipes.map(recipe => (
                                    <TableRow key={recipe.id} data-testid={`recipe-row-${recipe.id}`}>
                                        <TableCell className="font-medium">
                                            <button
                                                onClick={() => setDetailRecipe(recipe)}
                                                className="text-left hover:text-amber-600"
                                            >
                                                {recipe.name}
                                            </button>
                                        </TableCell>
                                        {canViewCosts && (
                                            <TableCell className="font-semibold">
                                                {recipe.sale_price_formatted || '-'}
                                            </TableCell>
                                        )}
                                        {canViewCosts && (
                                            <TableCell>{recipe.cost_price_formatted || '-'}</TableCell>
                                        )}
                                        {canViewCosts && (
                                            <TableCell>
                                                <span className={
                                                    recipe.margin_percent > 50 ? 'text-green-600 font-semibold' :
                                                    recipe.margin_percent > 20 ? 'text-amber-600' :
                                                    'text-red-600 font-semibold'
                                                }>
                                                    {recipe.margin_formatted || '-'}
                                                    {recipe.margin_percent !== null && (
                                                        <span className="text-xs ml-1">({recipe.margin_percent}%)</span>
                                                    )}
                                                </span>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Badge variant="secondary">
                                                {recipe.ingredients?.length || 0} ingrédients
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {recipe.is_active !== false ? (
                                                <Badge className="bg-green-100 text-green-800">Actif</Badge>
                                            ) : (
                                                <Badge variant="secondary">Inactif</Badge>
                                            )}
                                        </TableCell>
                                        {canEdit && (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(recipe)}
                                                        data-testid={`edit-recipe-${recipe.id}`}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    {canDelete && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(recipe.id)}
                                                            className="text-red-600 hover:text-red-700"
                                                            data-testid={`delete-recipe-${recipe.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Dialog Création/Édition */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingRecipe ? 'Modifier la recette' : 'Nouvelle recette'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">Nom de la recette *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="Ex: Thieb poulet"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="sale_price">Prix de vente (CFA) *</Label>
                                <Input
                                    id="sale_price"
                                    type="number"
                                    value={formData.sale_price_cfa}
                                    onChange={(e) => setFormData({...formData, sale_price_cfa: e.target.value})}
                                    placeholder="Ex: 2000"
                                    required
                                />
                            </div>
                        </div>

                        {/* Ingrédients */}
                        <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <Label>Ingrédients *</Label>
                                <Button type="button" variant="outline" size="sm" onClick={addIngredient}>
                                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                                </Button>
                            </div>
                            
                            <div className="space-y-3">
                                {formData.ingredients.length === 0 ? (
                                    <p className="text-gray-500 text-sm text-center py-4">
                                        Aucun ingrédient. Cliquez sur "Ajouter" pour commencer.
                                    </p>
                                ) : (
                                    formData.ingredients.map((ing, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <div className="flex-1">
                                                <Select 
                                                    value={ing.product_id} 
                                                    onValueChange={(v) => updateIngredient(index, 'product_id', v)}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Produit" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {products.map(p => (
                                                            <SelectItem key={p.id} value={p.id}>
                                                                {p.name} ({p.unit_base})
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="w-32">
                                                <Input
                                                    type="number"
                                                    step="0.1"
                                                    value={ing.quantity_base}
                                                    onChange={(e) => updateIngredient(index, 'quantity_base', e.target.value)}
                                                    placeholder="Qté"
                                                />
                                            </div>
                                            <span className="text-sm text-gray-500 w-16">
                                                {products.find(p => p.id === ing.product_id)?.unit_base || ''}
                                            </span>
                                            <Button 
                                                type="button" 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={() => removeIngredient(index)}
                                                className="text-red-600"
                                            >
                                                <Minus className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => setDialogOpen(false)}
                            >
                                Annuler
                            </Button>
                            <Button type="submit" data-testid="submit-recipe-btn">
                                {editingRecipe ? 'Mettre à jour' : 'Créer'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog Détail Recette */}
            <Dialog open={!!detailRecipe} onOpenChange={(open) => !open && setDetailRecipe(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UtensilsCrossed className="w-5 h-5" />
                            {detailRecipe?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {detailRecipe && (
                        <div className="space-y-4">
                            {canViewCosts && (
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-sm text-gray-500">Prix vente</p>
                                        <p className="text-lg font-bold">{detailRecipe.sale_price_formatted}</p>
                                    </div>
                                    {canViewCosts && (
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <p className="text-sm text-gray-500">Coût</p>
                                            <p className="text-lg font-bold">{detailRecipe.cost_price_formatted}</p>
                                        </div>
                                    )}
                                    {canViewCosts && (
                                        <div className={`p-3 rounded-lg ${
                                            detailRecipe.margin_percent > 30 ? 'bg-green-50' : 'bg-red-50'
                                        }`}>
                                            <p className="text-sm text-gray-500">Marge</p>
                                            <p className={`text-lg font-bold ${
                                                detailRecipe.margin_percent > 30 ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {detailRecipe.margin_percent}%
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div>
                                <h4 className="font-medium mb-2">Ingrédients</h4>
                                <div className="space-y-2">
                                    {detailRecipe.ingredients?.map((ing, idx) => (
                                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                            <span>{ing.product_name}</span>
                                            <span className="font-medium">
                                                {ing.quantity_display}
                                                {canViewCosts && ing.cost_formatted && (
                                                    <span className="text-gray-500 text-sm ml-2">
                                                        ({ing.cost_formatted})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RecipesPage;
